import { type DependencyList, useCallback, useEffect, useState } from 'react'

export type AsyncState<T> = {
    data: T | null
    loading: boolean
    error: Error | null
    reload: () => void
}

/** Run an async loader and expose its loading and error state. */
export function useAsync<T>(loader: () => Promise<T>, deps: DependencyList): AsyncState<T> {
    const [data, setData] = useState<T | null>(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<Error | null>(null)
    const [attempt, setAttempt] = useState(0)

    const reload = useCallback(() => setAttempt((value) => value + 1), [])

    useEffect(() => {
        let active = true
        setLoading(true)
        setError(null)
        loader()
            .then((result) => {
                if (active) {
                    setData(result)
                }
            })
            .catch((cause: unknown) => {
                if (active) {
                    setError(cause instanceof Error ? cause : new Error(String(cause)))
                }
            })
            .finally(() => {
                if (active) {
                    setLoading(false)
                }
            })
        return () => {
            active = false
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [...deps, attempt])

    return { data, loading, error, reload }
}

/** Debounce a rapidly changing value, such as a search field. */
export function useDebounced<T>(value: T, delay = 250): T {
    const [debounced, setDebounced] = useState(value)

    useEffect(() => {
        const timer = window.setTimeout(() => setDebounced(value), delay)
        return () => window.clearTimeout(timer)
    }, [value, delay])

    return debounced
}
